import { useRef } from "react";
import { Grid, IconButton, styled } from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import CloseIcon from "@mui/icons-material/Close";
import {
  VehiclePhotoAngle,
  VehiclePhotoInfo,
  VehiclePhotoPhase,
} from "../../../system/types/Vehicle";
import { CustomText } from "../../../styles/CustomText";
import {
  GRAYPALEBACKGROUND1,
  GRAYPALEBORDER,
  GRAYPALECONTENTS,
  GRAYPALETITLE,
  GREENCONTENTS,
  REDTITLE,
} from "../../../styles/color";
import { useSasToken } from "../../../hooks/useSasToken";
import AlertModal from "../../Common/AlertModal";
import {
  ANGLE_LABEL,
  MAX_ETC_PHOTOS,
  REQUIRED_ANGLES,
  VehiclePhotoDraft,
  createDraft,
  draftImageSrc,
  etcCount,
  revokeDraft,
} from "./vehiclePhoto";

interface VehiclePhotoUploaderProps {
  phase: VehiclePhotoPhase;
  drafts: VehiclePhotoDraft[];
  onChange: (drafts: VehiclePhotoDraft[]) => void;
  /** 운행 종료 때 같은 방향의 출발 사진을 빈 칸에 흐리게 보여줘서 같은 각도로 찍게 한다 */
  referencePhotos?: VehiclePhotoInfo[];
  /** false 면 4장 필수 표시를 하지 않는다 (관리자 수정) */
  required?: boolean;
  /** true 면 칸을 눌렀을 때 앨범이 먼저 열린다 (관리자가 받은 사진을 붙일 때) */
  preferGallery?: boolean;
}

/**
 * 차량 사진 선택 화면. 앞/뒤/좌/우 한 칸씩 + 추가 사진 최대 6장.
 * 칸을 누르면 카메라가 먼저 열리고, "앨범" 으로 저장된 사진도 고를 수 있다.
 * 업로드는 여기서 하지 않는다 — 부모가 저장 시점에 uploadVehiclePhotos 로 올린다.
 */
function VehiclePhotoUploader({
  phase,
  drafts,
  onChange,
  referencePhotos = [],
  required = true,
  preferGallery = false,
}: VehiclePhotoUploaderProps) {
  const { appendSas } = useSasToken();
  // label/htmlFor 대신 ref 로 input 을 연다. 같은 화면이 두 번 그려져도 id 가 겹치지 않는다.
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const openInput = (key: string) => inputRefs.current[key]?.click();

  const draftOf = (angle: VehiclePhotoAngle) =>
    drafts.find((draft) => draft.angle === angle);

  const setSlot = (angle: VehiclePhotoAngle, files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    const replaced = draftOf(angle);
    if (replaced) revokeDraft(replaced);
    onChange([
      ...drafts.filter((draft) => draft.angle !== angle),
      createDraft(angle, file),
    ]);
  };

  const addEtc = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const room = MAX_ETC_PHOTOS - etcCount(drafts);
    if (files.length > room) {
      AlertModal(
        "check",
        `추가 사진은 최대 ${MAX_ETC_PHOTOS}장까지 등록할 수 있습니다.`
      );
    }
    const picked = Array.from(files).slice(0, Math.max(room, 0));
    if (picked.length === 0) return;
    onChange([...drafts, ...picked.map((file) => createDraft("ETC", file))]);
  };

  const remove = (key: string) => {
    const target = drafts.find((draft) => draft.key === key);
    if (target) revokeDraft(target);
    onChange(drafts.filter((draft) => draft.key !== key));
  };

  const filledCount = REQUIRED_ANGLES.filter((angle) => draftOf(angle)).length;
  const etcDrafts = drafts.filter((draft) => draft.angle === "ETC");
  const canAddEtc = etcDrafts.length < MAX_ETC_PHOTOS;

  let hint = "차량 전체가 보이도록 앞·뒤·좌·우를 한 장씩 찍어주세요.";
  if (!required) {
    hint = "칸을 눌러 사진을 넣거나 바꿀 수 있습니다. 4장을 다 채우지 않아도 저장됩니다.";
  } else if (phase === "END") {
    hint = "출발 때와 같은 방향에서 앞·뒤·좌·우를 찍어주세요.";
  }
  // 칸을 눌렀을 때 먼저 여는 쪽과, 빈 칸 모서리 작은 버튼으로 여는 쪽
  const primarySource = preferGallery ? "gallery" : "camera";
  const secondarySource = preferGallery ? "camera" : "gallery";

  return (
    <Grid container>
      <Grid container item xs={12} justifyContent="space-between" alignItems="center">
        <CustomText type="subtitle" bold400 color={GRAYPALETITLE}>
          {required ? "차량 사진 (필수 4장)" : "차량 사진"}
        </CustomText>
        <CountText
          tone={
            !required
              ? "neutral"
              : filledCount === REQUIRED_ANGLES.length
              ? "complete"
              : "incomplete"
          }
        >
          {filledCount}/{REQUIRED_ANGLES.length}
        </CountText>
      </Grid>
      <Grid item xs={12}>
        {/* CustomText 는 기본이 줄바꿈 금지라 좁은 화면에서 잘린다 */}
        <CustomText type="small" color={GRAYPALECONTENTS} lineSpace>
          {hint}
        </CustomText>
      </Grid>

      <Grid container item xs={12} spacing={1} mt="4px">
        {REQUIRED_ANGLES.map((angle) => {
          const draft = draftOf(angle);
          const reference = referencePhotos.find((photo) => photo.angle === angle);
          const cameraKey = `${angle}-camera`;
          const galleryKey = `${angle}-gallery`;
          const primaryKey = `${angle}-${primarySource}`;
          const secondaryKey = `${angle}-${secondarySource}`;
          const emptyAction = preferGallery ? "사진 선택" : "촬영";
          return (
            <Grid item xs={6} key={angle}>
              <SlotFrame>
                <SlotTile
                  onClick={() => openInput(primaryKey)}
                  role="button"
                  aria-label={`${ANGLE_LABEL[angle]} ${
                    draft
                      ? `사진 ${preferGallery ? "바꾸기" : "다시 찍기"}`
                      : emptyAction
                  }`}
                >
                  {draft ? (
                    <SlotImage src={draftImageSrc(draft, appendSas)} alt={ANGLE_LABEL[angle]} />
                  ) : (
                    <>
                      {reference && (
                        <ReferenceImage
                          src={appendSas(reference.url)}
                          alt={`출발 ${ANGLE_LABEL[angle]}`}
                        />
                      )}
                      <EmptyBody>
                        {preferGallery ? (
                          <PhotoLibraryIcon sx={{ color: GRAYPALECONTENTS }} />
                        ) : (
                          <PhotoCameraIcon sx={{ color: GRAYPALECONTENTS }} />
                        )}
                        <CustomText type="small" color={GRAYPALETITLE}>
                          {ANGLE_LABEL[angle]} {emptyAction}
                        </CustomText>
                        {reference && (
                          <CustomText type="small" color={GRAYPALECONTENTS}>
                            (출발 사진 참고)
                          </CustomText>
                        )}
                      </EmptyBody>
                    </>
                  )}
                  <SlotLabel filled={!!draft}>{ANGLE_LABEL[angle]}</SlotLabel>
                  {draft ? (
                    <>
                      <RetakeHint>{preferGallery ? "바꾸기" : "다시 찍기"}</RetakeHint>
                      <RemoveButton
                        size="small"
                        aria-label="사진 삭제"
                        onClick={(event) => {
                          event.stopPropagation();
                          remove(draft.key);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </RemoveButton>
                    </>
                  ) : (
                    <GalleryButton
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openInput(secondaryKey);
                      }}
                    >
                      {preferGallery ? (
                        <>
                          <PhotoCameraIcon sx={{ fontSize: 16, mr: "2px" }} />
                          촬영
                        </>
                      ) : (
                        <>
                          <PhotoLibraryIcon sx={{ fontSize: 16, mr: "2px" }} />
                          앨범
                        </>
                      )}
                    </GalleryButton>
                  )}
                </SlotTile>
              </SlotFrame>
              <HiddenInput
                type="file"
                accept="image/*"
                capture="environment"
                ref={(element) => {
                  inputRefs.current[cameraKey] = element;
                }}
                onChange={(event) => {
                  setSlot(angle, event.target.files);
                  event.target.value = "";
                }}
              />
              <HiddenInput
                type="file"
                accept="image/*"
                ref={(element) => {
                  inputRefs.current[galleryKey] = element;
                }}
                onChange={(event) => {
                  setSlot(angle, event.target.files);
                  event.target.value = "";
                }}
              />
            </Grid>
          );
        })}
      </Grid>

      <Grid container item xs={12} mt="14px" alignItems="center" justifyContent="space-between">
        <CustomText type="subtitle" bold400 color={GRAYPALETITLE}>
          추가 사진 (선택)
        </CustomText>
        <CustomText type="small" color={GRAYPALECONTENTS}>
          {etcDrafts.length}/{MAX_ETC_PHOTOS}
        </CustomText>
      </Grid>
      <Grid item xs={12}>
        <CustomText type="small" color={GRAYPALECONTENTS} lineSpace>
          흠집이나 파손 부위가 있으면 가까이에서 찍어 남겨주세요.
        </CustomText>
      </Grid>
      <EtcRow>
        {etcDrafts.map((draft) => (
          <EtcItem key={draft.key}>
            <EtcImage src={draftImageSrc(draft, appendSas)} alt="추가 사진" />
            <EtcRemoveButton
              size="small"
              aria-label="추가 사진 삭제"
              onClick={() => remove(draft.key)}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </EtcRemoveButton>
          </EtcItem>
        ))}
        {canAddEtc && (
          <>
            <EtcAddTile type="button" onClick={() => openInput("etc-camera")}>
              <PhotoCameraIcon fontSize="small" />
              <span>촬영</span>
            </EtcAddTile>
            <EtcAddTile type="button" onClick={() => openInput("etc-gallery")}>
              <PhotoLibraryIcon fontSize="small" />
              <span>앨범</span>
            </EtcAddTile>
          </>
        )}
      </EtcRow>
      <HiddenInput
        type="file"
        accept="image/*"
        capture="environment"
        ref={(element) => {
          inputRefs.current["etc-camera"] = element;
        }}
        onChange={(event) => {
          addEtc(event.target.files);
          event.target.value = "";
        }}
      />
      <HiddenInput
        type="file"
        accept="image/*"
        multiple
        ref={(element) => {
          inputRefs.current["etc-gallery"] = element;
        }}
        onChange={(event) => {
          addEtc(event.target.files);
          event.target.value = "";
        }}
      />
    </Grid>
  );
}

export default VehiclePhotoUploader;

const COUNT_COLOR = {
  complete: GREENCONTENTS,
  incomplete: REDTITLE,
  neutral: GRAYPALECONTENTS,
};

const CountText = styled("span", {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: keyof typeof COUNT_COLOR }>(({ tone }) => ({
  fontSize: "0.9rem",
  fontWeight: 600,
  color: COUNT_COLOR[tone],
}));

// 4:3 비율 칸. aspect-ratio 를 못 쓰는 브라우저를 위해 padding 으로 높이를 잡는다.
const SlotFrame = styled("div")({
  position: "relative",
  width: "100%",
  paddingTop: "75%",
});

// inset 대신 top/right/bottom/left — 구형 iOS Safari 도 지원하도록
const SlotTile = styled("div")({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  border: `1px dashed ${GRAYPALEBORDER}`,
  borderRadius: "8px",
  backgroundColor: GRAYPALEBACKGROUND1,
  overflow: "hidden",
  cursor: "pointer",
});

const SlotImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

const ReferenceImage = styled("img")({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  opacity: 0.3,
});

const EmptyBody = styled("div")({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "2px",
});

const SlotLabel = styled("span")<{ filled: boolean }>(({ filled }) => ({
  position: "absolute",
  top: "6px",
  left: "6px",
  padding: "1px 8px",
  borderRadius: "10px",
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#fff",
  backgroundColor: filled ? GREENCONTENTS : GRAYPALECONTENTS,
}));

const RetakeHint = styled("span")({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  padding: "3px 0",
  textAlign: "center",
  fontSize: "0.75rem",
  color: "#fff",
  backgroundColor: "rgba(0, 0, 0, 0.45)",
});

const RemoveButton = styled(IconButton)({
  position: "absolute",
  top: "2px",
  right: "2px",
  color: "#fff",
  backgroundColor: "rgba(0, 0, 0, 0.45)",
  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.65)" },
});

const GalleryButton = styled("button")({
  position: "absolute",
  bottom: "6px",
  right: "6px",
  display: "inline-flex",
  alignItems: "center",
  border: `1px solid ${GRAYPALEBORDER}`,
  borderRadius: "12px",
  backgroundColor: "#fff",
  color: GRAYPALETITLE,
  fontSize: "0.75rem",
  padding: "2px 8px",
  cursor: "pointer",
});

const HiddenInput = styled("input")({
  display: "none",
});

const EtcRow = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  width: "100%",
  marginTop: "6px",
});

const EtcItem = styled("div")({
  position: "relative",
  width: "72px",
  height: "72px",
});

const EtcImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "8px",
  border: `1px solid ${GRAYPALEBORDER}`,
  display: "block",
});

const EtcRemoveButton = styled(IconButton)({
  position: "absolute",
  top: "-6px",
  right: "-6px",
  padding: "2px",
  color: "#fff",
  backgroundColor: "rgba(0, 0, 0, 0.55)",
  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.75)" },
});

const EtcAddTile = styled("button")({
  width: "72px",
  height: "72px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "2px",
  border: `1px dashed ${GRAYPALEBORDER}`,
  borderRadius: "8px",
  backgroundColor: GRAYPALEBACKGROUND1,
  color: GRAYPALECONTENTS,
  fontSize: "0.75rem",
  cursor: "pointer",
});
